// frontend/src/pages/superAdmin/ManageQuestionnaires.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../services/api';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';

const ManageQuestionnaires = ({ user, setUser }) => {
  const [fiscalYears, setFiscalYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedType, setSelectedType] = useState('1'); // Default to IIT
  const [topics, setTopics] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  
  // Modals state
  const [showTopicModal, setShowTopicModal] = useState(false);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  
  // Form data
  const [currentTopic, setCurrentTopic] = useState({
    id: null,
    name: '',
    description: ''
  });
  
  // เพิ่มจำนวนตัวเลือกจาก 4 เป็น 6 และเพิ่ม field description
  const [currentQuestion, setCurrentQuestion] = useState({
    id: null,
    question_text: '',
    question_type: 'multiple_choice',
    weight: 1,
    topic_id: '',
    description: '', // เพิ่ม field สำหรับคำอธิบายเพิ่มเติม
    explanation_link: '', // เพิ่มฟิลด์นี้
    options: [
      { option_text: '', score: 0 },
      { option_text: '', score: 0 },
      { option_text: '', score: 0 },
      { option_text: '', score: 0 },
      { option_text: '', score: 0 },
      { option_text: '', score: 0 }
    ]
  });
  
  const [selectedTopicId, setSelectedTopicId] = useState('');

  // Questionnaire types
  const questionnaireTypes = [
    { id: '1', name: 'IIT (บุคลากรภายในหน่วยงาน)' },
    { id: '2', name: 'EIT1 (ผู้มีส่วนได้ส่วนเสียโรงเรียนกำหนด)' },
    { id: '3', name: 'EIT2 (ผู้มีส่วนได้ส่วนเสียทั่วไป)' },
    { id: '4', name: 'OIT (การเปิดเผยข้อมูลสาธารณะ)' }
  ];

  // Fetch fiscal years data
  const fetchFiscalYears = async () => {
    try {
      setLoading(true);
      const response = await api.get('/fiscal-years');
      setFiscalYears(response.data);
      
      // Set default selected year to active year
      const activeYear = response.data.find(year => year.active);
      if (activeYear) {
        setSelectedYear(activeYear.id);
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถโหลดข้อมูลปีงบประมาณได้',
        confirmButtonText: 'ตกลง'
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch topics by questionnaire type and fiscal year
  const fetchTopics = async () => {
    if (!selectedYear || !selectedType) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/topics/${selectedType}/${selectedYear}`);
      setTopics(response.data);
      
      // Set default selected topic
      if (response.data.length > 0) {
        setSelectedTopicId(response.data[0].id);
      } else {
        setSelectedTopicId('');
        setQuestions([]);
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถโหลดข้อมูลหัวข้อแบบสอบถามได้',
        confirmButtonText: 'ตกลง'
      });
      setTopics([]);
      setSelectedTopicId('');
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch questions by topic
  const fetchQuestions = async (topicId) => {
    if (!topicId) {
      setQuestions([]);
      return;
    }
    
    try {
      setLoadingQuestions(true);
      const response = await api.get(`/questions/topic/${topicId}`);
      setQuestions(response.data);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถโหลดข้อมูลคำถามได้',
        confirmButtonText: 'ตกลง'
      });
      setQuestions([]);
    } finally {
      setLoadingQuestions(false);
    }
  };

  useEffect(() => {
    fetchFiscalYears();
  }, []);

  useEffect(() => {
    if (selectedYear && selectedType) {
      fetchTopics();
    }
  }, [selectedYear, selectedType]);

  useEffect(() => {
    if (selectedTopicId) {
      fetchQuestions(selectedTopicId);
    }
  }, [selectedTopicId]);

  // Handle fiscal year change
  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
  };

  // Handle questionnaire type change
  const handleTypeChange = (e) => {
    setSelectedType(e.target.value);
  };

  // Handle topic change
  const handleTopicChange = (e) => {
    setSelectedTopicId(e.target.value);
  };

  // Handle topic input change
  const handleTopicInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentTopic({
      ...currentTopic,
      [name]: value
    });
  };

  // Handle question input change
  const handleQuestionInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentQuestion({
      ...currentQuestion,
      [name]: value
    });
  };

  // Handle option input change
  const handleOptionChange = (index, field, value) => {
    const updatedOptions = [...currentQuestion.options];
    updatedOptions[index] = {
      ...updatedOptions[index],
      [field]: field === 'score' ? parseFloat(value) : value
    };
    
    setCurrentQuestion({
      ...currentQuestion,
      options: updatedOptions
    });
  };

  // Open add topic modal
  const handleAddTopicClick = () => {
    setCurrentTopic({
      id: null,
      name: '',
      description: ''
    });
    setEditMode(false);
    setShowTopicModal(true);
  };

  // Open edit topic modal
  const handleEditTopicClick = (topic) => {
    setCurrentTopic({
      id: topic.id,
      name: topic.name,
      description: topic.description || ''
    });
    setEditMode(true);
    setShowTopicModal(true);
  };

  // Open add question modal
  const handleAddQuestionClick = () => {
    // รีเซ็ตข้อมูลคำถามพร้อมตัวเลือก 6 ตัว
    setCurrentQuestion({
      id: null,
      question_text: '',
      question_type: selectedType === '4' ? 'link' : 'multiple_choice',
      weight: 1,
      topic_id: selectedTopicId,
      description: '', // เพิ่มการรีเซ็ต description
      options: [
        { option_text: '', score: 0 },
        { option_text: '', score: 0 },
        { option_text: '', score: 0 },
        { option_text: '', score: 0 },
        { option_text: '', score: 0 },
        { option_text: '', score: 0 }
      ]
    });
    setEditMode(false);
    setShowQuestionModal(true);
  };

  // Open edit question modal
  const handleEditQuestionClick = (question) => {
    const questionData = {
      id: question.id,
      question_text: question.question_text,
      question_type: question.question_type,
      weight: question.weight,
      topic_id: selectedTopicId,
      description: question.description || '', // เพิ่มการดึงข้อมูล description
      explanation_link: question.explanation_link || '', // เพิ่มบรรทัดนี้
      options: question.options || [
        { option_text: '', score: 0 },
        { option_text: '', score: 0 },
        { option_text: '', score: 0 },
        { option_text: '', score: 0 },
        { option_text: '', score: 0 },
        { option_text: '', score: 0 }
      ]
    };
    
    // ปรับแก้ให้รองรับ 6 ตัวเลือก
    if (question.question_type === 'multiple_choice' && questionData.options.length < 6) {
      const additionalOptions = Array(6 - questionData.options.length).fill().map(() => ({ option_text: '', score: 0 }));
      questionData.options = [...questionData.options, ...additionalOptions];
    }
    
    setCurrentQuestion(questionData);
    setEditMode(true);
    setShowQuestionModal(true);
  };

  // Handle topic form submit
  const handleTopicSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editMode) {
        // Update topic
        await api.put(`/topics/${currentTopic.id}`, {
          name: currentTopic.name,
          description: currentTopic.description
        });
        
        Swal.fire({
          icon: 'success',
          title: 'สำเร็จ',
          text: 'อัปเดตหัวข้อแบบสอบถามเรียบร้อยแล้ว',
          confirmButtonText: 'ตกลง'
        });
      } else {
        // Create topic
        await api.post('/topics', {
          questionnaire_type_id: selectedType,
          fiscal_year_id: selectedYear,
          name: currentTopic.name,
          description: currentTopic.description
        });
        
        Swal.fire({
          icon: 'success',
          title: 'สำเร็จ',
          text: 'เพิ่มหัวข้อแบบสอบถามเรียบร้อยแล้ว',
          confirmButtonText: 'ตกลง'
        });
      }
      
      // Refresh topics
      fetchTopics();
      setShowTopicModal(false);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: error.response?.data?.message || 'ไม่สามารถบันทึกข้อมูลได้',
        confirmButtonText: 'ตกลง'
      });
    }
  };

  // Handle question form submit
  const handleQuestionSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Prepare options data for multiple choice questions
      const questionData = { ...currentQuestion };
      if (questionData.question_type === 'multiple_choice') {
        // Filter out empty options
        questionData.options = questionData.options.filter(option => option.option_text.trim() !== '');
        
        // Check if there are at least 2 options
        if (questionData.options.length < 2) {
          Swal.fire({
            icon: 'warning',
            title: 'ข้อมูลไม่ครบถ้วน',
            text: 'กรุณากรอกตัวเลือกอย่างน้อย 2 ตัวเลือก',
            confirmButtonText: 'ตกลง'
          });
          return;
        }
      }
      
      if (editMode) {
        // Update question
        await api.put(`/questions/${currentQuestion.id}`, questionData);
        
        Swal.fire({
          icon: 'success',
          title: 'สำเร็จ',
          text: 'อัปเดตคำถามเรียบร้อยแล้ว',
          confirmButtonText: 'ตกลง'
        });
      } else {
        // Create question
        await api.post('/questions', questionData);
        
        Swal.fire({
          icon: 'success',
          title: 'สำเร็จ',
          text: 'เพิ่มคำถามเรียบร้อยแล้ว',
          confirmButtonText: 'ตกลง'
        });
      }
      
      // Refresh questions
      fetchQuestions(selectedTopicId);
      setShowQuestionModal(false);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: error.response?.data?.message || 'ไม่สามารถบันทึกข้อมูลได้',
        confirmButtonText: 'ตกลง'
      });
    }
  };

  // Get questionnaire type text
  const getQuestionnaireTypeText = () => {
    const type = questionnaireTypes.find(t => t.id === selectedType);
    return type ? type.name : '';
  };

  // Get fiscal year text
  const getFiscalYearText = () => {
    const year = fiscalYears.find(y => y.id.toString() === selectedYear.toString());
    return year ? year.year : '';
  };

  return (
    <div className="wrapper">
      <Header user={user} title="จัดการแบบสอบถาม" />
      <Sidebar user={user} />

      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">จัดการแบบสอบถาม</h1>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-right">
                  <li className="breadcrumb-item">
                    <Link to="/super-admin/dashboard">หน้าหลัก</Link>
                  </li>
                  <li className="breadcrumb-item active">จัดการแบบสอบถาม</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        <section className="content">
          <div className="container-fluid">
            <div className="card card-navy mb-4">
              <div className="card-header">
                <h3 className="card-title">เลือกแบบสอบถาม</h3>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <div className="form-group">
                      <label>ปีงบประมาณ</label>
                      <select
                        className="form-select"
                        value={selectedYear}
                        onChange={handleYearChange}
                      >
                        <option value="">-- เลือกปีงบประมาณ --</option>
                        {fiscalYears.map((year) => (
                          <option key={year.id} value={year.id}>
                            {year.year} {year.active ? '(ปัจจุบัน)' : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <div className="form-group">
                      <label>ประเภทแบบสอบถาม</label>
                      <select
                        className="form-select"
                        value={selectedType}
                        onChange={handleTypeChange}
                      >
                        {questionnaireTypes.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">กำลังโหลด...</span>
                </div>
                <p className="mt-2">กำลังโหลดข้อมูล...</p>
              </div>
            ) : (
              selectedYear && selectedType && (
                <div className="row">
                  <div className="col-md-4">
                    <div className="card card-primary">
                      <div className="card-header bg-navy">
                        <h3 className="card-title">
                          หัวข้อแบบสอบถาม {getQuestionnaireTypeText()} ปีงบประมาณ {getFiscalYearText()}
                        </h3>
                        <div className="card-tools">
                          <button
                            type="button"
                            className="btn btn-success btn-sm"
                            onClick={handleAddTopicClick}
                          >
                            <i className="fas fa-plus"></i>
                          </button>
                        </div>
                      </div>
                      <div className="card-body">
                        <div className="form-group">
                          <label>เลือกหัวข้อ</label>
                          <select
                            className="form-select"
                            value={selectedTopicId}
                            onChange={handleTopicChange}
                          >
                            <option value="">-- เลือกหัวข้อ --</option>
                            {topics.map((topic) => (
                              <option key={topic.id} value={topic.id}>
                                {topic.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        {topics.length === 0 ? (
                          <div className="alert alert-info mt-3">
                            ยังไม่มีหัวข้อแบบสอบถาม กรุณาเพิ่มหัวข้อ
                          </div>
                        ) : (
                          <div className="list-group mt-3">
                            {topics.map((topic) => (
                              <div
                                key={topic.id}
                                className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${
                                  selectedTopicId === topic.id ? 'active' : ''
                                }`}
                                onClick={() => setSelectedTopicId(topic.id)}
                                style={{ cursor: 'pointer' }}
                              >
                                <div>
                                  <h6 className="mb-1">{topic.name}</h6>
                                  {topic.description && (
                                    <small>{topic.description}</small>
                                  )}
                                </div>
                                <button
                                  className="btn btn-sm btn-info"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditTopicClick(topic);
                                  }}
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-8">
                    <div className="card card-primary">
                      <div className="card-header bg-navy">
                        <h3 className="card-title">
                          รายการคำถาม
                          {selectedTopicId && topics.find(t => t.id === selectedTopicId) && 
                            ` - ${topics.find(t => t.id === selectedTopicId).name}`}
                        </h3>
                        <div className="card-tools">
                          {selectedTopicId && (
                            <button
                              type="button"
                              className="btn btn-success btn-sm"
                              onClick={handleAddQuestionClick}
                            >
                              <i className="fas fa-plus"></i> เพิ่มคำถาม
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="card-body">
                        {!selectedTopicId ? (
                          <div className="alert alert-info">
                            กรุณาเลือกหัวข้อแบบสอบถาม
                          </div>
                        ) : loadingQuestions ? (
                          <div className="text-center py-3">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">กำลังโหลด...</span>
                            </div>
                            <p className="mt-2">กำลังโหลดข้อมูลคำถาม...</p>
                          </div>
                        ) : questions.length === 0 ? (
                          <div className="alert alert-info">
                            ยังไม่มีคำถามในหัวข้อนี้ กรุณาเพิ่มคำถาม
                          </div>
                        ) : (
                          <div className="table-responsive">
                            <table className="table table-bordered table-striped">
                              <thead>
                                <tr className="bg-navy">
                                  <th style={{ width: '5%' }}>ลำดับ</th>
                                  <th style={{ width: '50%' }}>คำถาม</th>
                                  <th style={{ width: '15%' }}>ประเภท</th>
                                  <th style={{ width: '10%' }}>น้ำหนัก</th>
                                  <th style={{ width: '20%' }}>จัดการ</th>
                                </tr>
                              </thead>
                              <tbody>
                                {questions.map((question, index) => (
                                  <tr key={question.id}>
                                    <td className="text-center">{index + 1}</td>
                                    <td>
                                      {question.question_text}
                                      {/* แสดงคำอธิบายเพิ่มเติมสำหรับ OIT */}
                                      {selectedType === '4' && question.description && (
                                        <div className="mt-2 small text-muted">
                                          <strong>คำอธิบายเพิ่มเติม:</strong> {question.description}
                                        </div>
                                      )}
                                      {/* แสดงไอคอนลิงก์อธิบายเพิ่มเติม (ถ้ามี) */}
  {selectedType === '4' && question.explanation_link && (
    <div className="mt-1">
      <i className="fas fa-link text-info me-1"></i>
      <small>มีลิงก์อธิบายเพิ่มเติม</small>
    </div>
  )}
                                    </td>
                                    <td>
                                      {question.question_type === 'multiple_choice'
                                        ? 'แบบตัวเลือก'
                                        : 'แบบลิงก์'}
                                    </td>
                                    <td className="text-center">{question.weight}</td>
                                    <td className="text-center">
                                      <button
                                        className="btn btn-info btn-sm"
                                        onClick={() => handleEditQuestionClick(question)}
                                      >
                                        <i className="fas fa-edit"></i> แก้ไข
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )
            )}
          </div>
        </section>
      </div>

      <Footer />

      {/* Modal for Add/Edit Topic */}
      {showTopicModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header bg-navy">
                <h5 className="modal-title">
                  {editMode ? 'แก้ไขหัวข้อแบบสอบถาม' : 'เพิ่มหัวข้อแบบสอบถาม'}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowTopicModal(false)}
                ></button>
              </div>
              <form onSubmit={handleTopicSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label htmlFor="name" className="form-label">
                      ชื่อหัวข้อ <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="name"
                      name="name"
                      value={currentTopic.name}
                      onChange={handleTopicInputChange}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="description" className="form-label">
                      คำอธิบาย
                    </label>
                    <textarea
                      className="form-control"
                      id="description"
                      name="description"
                      rows="3"
                      value={currentTopic.description || ''}
                      onChange={handleTopicInputChange}
                    ></textarea>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowTopicModal(false)}
                  >
                    ยกเลิก
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editMode ? 'บันทึกการแก้ไข' : 'เพิ่มหัวข้อ'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal for Add/Edit Question */}
      {showQuestionModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-navy">
                <h5 className="modal-title">
                  {editMode ? 'แก้ไขคำถาม' : 'เพิ่มคำถาม'}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowQuestionModal(false)}
                ></button>
              </div>
              <form onSubmit={handleQuestionSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label htmlFor="question_text" className="form-label">
                      คำถาม <span className="text-danger">*</span>
                    </label>
                    <textarea
                      className="form-control"
                      id="question_text"
                      name="question_text"
                      rows="3"
                      value={currentQuestion.question_text}
                      onChange={handleQuestionInputChange}
                      required
                    ></textarea>
                  </div>
                  
                  {/* เพิ่มฟิลด์คำอธิบายเพิ่มเติมสำหรับ OIT */}
                  {selectedType === '4' && (
  <>
    <div className="mb-3">
      <label htmlFor="description" className="form-label">
        คำอธิบายเพิ่มเติม 
        <span className="text-muted ms-2">(จะแสดงในหน้า OIT ของโรงเรียน)</span>
      </label>
      <textarea
        className="form-control"
        id="description"
        name="description"
        rows="3"
        value={currentQuestion.description || ''}
        onChange={handleQuestionInputChange}
        placeholder="อธิบายรายละเอียดเพิ่มเติมเกี่ยวกับตัวชี้วัดนี้ เช่น ข้อมูลที่ต้องการ, รูปแบบการนำเสนอ, เกณฑ์การให้คะแนน เป็นต้น"
      ></textarea>
    </div>
    <div className="mb-3">
      <label htmlFor="explanation_link" className="form-label">
        ลิงก์อธิบายเพิ่มเติม 
        <span className="text-muted ms-2">(เช่น คู่มือ, ตัวอย่าง หรือเอกสารอธิบาย)</span>
      </label>
      <input
        type="url"
        className="form-control"
        id="explanation_link"
        name="explanation_link"
        value={currentQuestion.explanation_link || ''}
        onChange={handleQuestionInputChange}
        placeholder="https://example.com/guide"
      />
      <div className="form-text">ใส่ URL ที่นำไปสู่เอกสารหรือคู่มืออธิบายเพิ่มเติม (ถ้ามี)</div>
    </div>
  </>
)}
                  
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label htmlFor="question_type" className="form-label">
                        ประเภทคำถาม <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        id="question_type"
                        name="question_type"
                        value={currentQuestion.question_type}
                        onChange={handleQuestionInputChange}
                        disabled={selectedType === '4'} // Disable for OIT
                      >
                        <option value="multiple_choice">แบบตัวเลือก</option>
                        <option value="link">แบบลิงก์</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="weight" className="form-label">
                        น้ำหนักคะแนน <span className="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="weight"
                        name="weight"
                        value={currentQuestion.weight}
                        onChange={handleQuestionInputChange}
                        min="0.01"
                        step="0.01"
                        required
                      />
                    </div>
                  </div>
                  
                  {currentQuestion.question_type === 'multiple_choice' && (
                    <div>
                      <h5 className="mt-4 mb-3">ตัวเลือกคำตอบ</h5>
                      
                      {/* เพิ่มแจ้งเตือนที่ชัดเจนขึ้น */}
                      <div className="alert alert-info mb-3">
                        <i className="fas fa-info-circle me-2"></i>
                        <strong>คำแนะนำ:</strong> กรุณากรอกข้อมูลตัวเลือกที่ต้องการใช้ (ต้องมีอย่างน้อย 2 ตัวเลือก) และกำหนดคะแนนให้แต่ละตัวเลือก ค่าคะแนนเต็ม 1
                      </div>
                      
                      {/* ส่วนแสดงตัวเลือก (เพิ่มเป็น 6 ตัวเลือก) */}
                      {currentQuestion.options.map((option, index) => (
                        <div key={index} className="row mb-3">
                          <div className="col-md-8">
                            <label htmlFor={`option_text_${index}`} className="form-label">
                              ตัวเลือกที่ {index + 1}
                              {index < 2 && <span className="text-danger">*</span>}
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              id={`option_text_${index}`}
                              value={option.option_text}
                              onChange={(e) => handleOptionChange(index, 'option_text', e.target.value)}
                              required={index < 2}
                            />
                          </div>
                          <div className="col-md-4">
                            <label htmlFor={`score_${index}`} className="form-label">
                              คะแนน (0-1)
                              {index < 2 && <span className="text-danger">*</span>}
                            </label>
                            <input
                              type="number"
                              className="form-control"
                              id={`score_${index}`}
                              value={option.score}
                              onChange={(e) => handleOptionChange(index, 'score', e.target.value)}
                              min="0"
                              max="1"
                              step="0.01"
                              required={index < 2}
                            />
                          </div>
                        </div>
                      ))}
                      <div className="form-text text-muted">
                        <i className="fas fa-info-circle me-1"></i>
                        กรุณากรอกตัวเลือกอย่างน้อย 2 ตัวเลือก (สูงสุด 6 ตัวเลือก) ตัวเลือกที่ไม่ได้กรอกจะไม่ถูกบันทึก
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowQuestionModal(false)}
                  >
                    ยกเลิก
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editMode ? 'บันทึกการแก้ไข' : 'เพิ่มคำถาม'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageQuestionnaires;